import { absoluteUrl, api } from './api';

// Error monitoring: crashes are always reported to the MailPilot server (built-in email alerts); Sentry is also
// loaded when the server is configured for it. No session replay, no
// tracing, and no request bodies, so email content never leaves the device.

type SentryModule = typeof import('@sentry/react');
let sentry: SentryModule | null = null;

const isDesktop = () => !!(window as any).mailpilotDesktop;
let sentThisSession = 0;

/** Send a crash report to our own server (at most 10 per page load; never throws). */
function reportToServer(err: unknown, where?: string): void {
  if (sentThisSession >= 10) return;
  sentThisSession++;
  const e = err instanceof Error ? err : new Error(String(err));
  void absoluteUrl('/api/client-errors').then((url) => fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ client: isDesktop() ? 'desktop' : 'web', name: e.name, message: e.message.slice(0, 2000), stack: e.stack?.slice(0, 8000) ?? null, where, version: (import.meta as any).env?.VITE_APP_VERSION }),
    keepalive: true,
  })).catch(() => undefined);
}

export async function initMonitoring(): Promise<void> {
  window.addEventListener('error', (ev) => ev.error && reportToServer(ev.error, 'window.onerror'));
  window.addEventListener('unhandledrejection', (ev) => {
    // API errors (4xx/5xx) are expected flows shown in the UI; only report genuine crashes.
    if (ev.reason && ev.reason.name !== 'ApiError') reportToServer(ev.reason, 'unhandledrejection');
  });
  try {
    const cfg = (await api('GET /api/auth/config')).monitoring;
    if (!cfg) return;
    const S = await import('@sentry/react');
    S.init({
      dsn: cfg.dsn,
      environment: cfg.environment,
      release: cfg.release ?? undefined,
      sendDefaultPii: false,
      tracesSampleRate: 0,
      initialScope: { tags: { client: (window as any).mailpilotDesktop ? 'desktop' : 'web' } },
      beforeBreadcrumb: (b) => (b.category === 'console' || b.category === 'ui.input' ? null : b),
      beforeSend(event) {
        if (event.request) event.request = { url: event.request.url?.split('?')[0] };
        return event;
      },
    });
    sentry = S;
  } catch {
    /* monitoring is best-effort */
  }
}

export function captureError(err: unknown, extra?: Record<string, string>): void {
  reportToServer(err, extra?.where);
  sentry?.withScope((scope) => {
    if (extra) for (const [k, v] of Object.entries(extra)) scope.setTag(k, v);
    sentry?.captureException(err);
  });
}
